import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { Trash, X } from '@phosphor-icons/react';
import { format } from 'date-fns';
import api from '../../utils/api';

function ViewAllShiftsDialog({ open, onClose, staffMember, businessId }) {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deletingShiftId, setDeletingShiftId] = useState(null);
  const [deleteMenuAnchor, setDeleteMenuAnchor] = useState(null);
  const [shiftToDelete, setShiftToDelete] = useState(null);
  const [orderBy, setOrderBy] = useState('startTime');
  const [order, setOrder] = useState('desc'); // 'asc' or 'desc'

  useEffect(() => {
    if (open && staffMember?._id) {
      fetchAllShifts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [open, staffMember]);

  const fetchAllShifts = async () => {
    if (!staffMember?._id || !businessId) return;

    setLoading(true);
    setError('');

    try {
      // Use a very wide date range to get all shifts
      const startDate = new Date('1900-01-01');
      const endDate = new Date('3000-12-31');
      
      const response = await api.get(
        `/api/team-member-shifts/team-member/${staffMember._id}`,
        {
          params: {
            start: startDate.toISOString(),
            end: endDate.toISOString()
          }
        }
      );

      setShifts(response.data?.shifts || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load shifts');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedShifts = React.useMemo(() => {
    if (!shifts.length) return [];

    return [...shifts].sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case 'startTime':
          aValue = new Date(a.startTime).getTime();
          bValue = new Date(b.startTime).getTime();
          break;
        case 'endTime':
          aValue = new Date(a.endTime).getTime();
          bValue = new Date(b.endTime).getTime();
          break;
        case 'duration':
          aValue = new Date(a.endTime).getTime() - new Date(a.startTime).getTime();
          bValue = new Date(b.endTime).getTime() - new Date(b.startTime).getTime();
          break;
        default:
          return 0;
      }

      if (order === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
  }, [shifts, orderBy, order]);

  const handleDeleteClick = (event, shift) => {
    event.stopPropagation();
    setShiftToDelete(shift);
    setDeleteMenuAnchor(event.currentTarget);
  };

  const handleDeleteMenuClose = () => {
    setDeleteMenuAnchor(null);
    setShiftToDelete(null);
  };

  const handleDeleteShift = async (deleteSeries = false) => {
    if (!shiftToDelete) return;

    const shift = shiftToDelete;
    const shiftId = shift._id;
    setDeletingShiftId(shiftId);
    handleDeleteMenuClose();

    try {
      const url = `/api/team-member-shifts/${shiftId}${deleteSeries ? '?deleteSeries=true' : ''}`;
      await api.delete(url);

      // Remove from local state
      if (deleteSeries && shift.seriesId) {
        const cutoff = new Date(shift.startTime).getTime();
        setShifts(prev => prev.filter(s => s.seriesId !== shift.seriesId || new Date(s.startTime).getTime() < cutoff));
      } else {
        setShifts(prev => prev.filter(s => s._id !== shiftId));
      }

      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete shift');
    } finally {
      setDeletingShiftId(null);
    }
  };

  const _formatTimeRange = (startTime, endTime) => {
    return `${format(new Date(startTime), 'HH:mm')} - ${format(new Date(endTime), 'HH:mm')}`;
  };

  const formatDuration = (startTime, endTime) => {
    const minutes = Math.round((new Date(endTime) - new Date(startTime)) / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const getStaffMemberName = () => {
    if (staffMember?.user) {
      const user = staffMember.user;
      return `${user.firstName || ''} ${user.lastName || ''}`.trim() || staffMember.name;
    }
    return staffMember?.name || 'Staff Member';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            All Shifts - {getStaffMemberName()}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : sortedShifts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No shifts found
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'startTime'}
                      direction={orderBy === 'startTime' ? order : 'asc'}
                      onClick={() => handleSort('startTime')}
                    >
                      Date & Start Time
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'endTime'}
                      direction={orderBy === 'endTime' ? order : 'asc'}
                      onClick={() => handleSort('endTime')}
                    >
                      End Time
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'duration'}
                      direction={orderBy === 'duration' ? order : 'asc'}
                      onClick={() => handleSort('duration')}
                    >
                      Duration
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Pattern</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedShifts.map((shift) => {
                  const startDate = new Date(shift.startTime);
                  const endDate = new Date(shift.endTime);
                  const isOutlier = startDate.getFullYear() < 2000 || startDate.getFullYear() > 2100;

                  return (
                    <TableRow
                      key={shift._id}
                      sx={{
                        backgroundColor: isOutlier ? 'warning.light' : 'transparent',
                        '&:hover': {
                          backgroundColor: isOutlier ? 'warning.light' : 'action.hover'
                        }
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2">
                          {format(startDate, 'MMM d, yyyy')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(startDate, 'HH:mm')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {format(endDate, 'HH:mm')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDuration(shift.startTime, shift.endTime)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {shift.seriesId ? (
                          <Typography variant="caption" color="primary">
                            Repeating
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Single
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteClick(e, shift)}
                          disabled={deletingShiftId === shift._id}
                          color="error"
                        >
                          {deletingShiftId === shift._id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Trash size={16} />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Delete confirmation menu */}
      <Menu
        anchorEl={deleteMenuAnchor}
        open={Boolean(deleteMenuAnchor)}
        onClose={handleDeleteMenuClose}
      >
        <MenuItem
          onClick={() => handleDeleteShift(false)}
          disabled={deletingShiftId === shiftToDelete?._id}
        >
          <ListItemIcon>
            <Trash size={16} />
          </ListItemIcon>
          <ListItemText>Delete this shift</ListItemText>
        </MenuItem>
        {shiftToDelete?.seriesId && (
          <MenuItem
            onClick={() => handleDeleteShift(true)}
            disabled={deletingShiftId === shiftToDelete?._id}
          >
            <ListItemIcon>
              <Trash size={16} />
            </ListItemIcon>
            <ListItemText>Delete this and future shifts</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Dialog>
  );
}

export default ViewAllShiftsDialog;
